/**
 * Budget Period Configuration Modal
 * 
 * Modal for creating and configuring a budget period.
 * 
 * Based on Back Log 2 - Período Orçamentário:
 * - O período é de 28-31 dias entre a Receita de um mês e a do mês seguinte
 * - Início: dia em que o usuário recebe sua principal Receita
 * - Fim: dia imediatamente anterior ao próximo recebimento
 * - Ex: Se recebe dia 5, período vai de 5 de um mês até 4 do mês seguinte
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Settings, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { budgetPeriodService } from '@/services/financial.service';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import type { BudgetPeriod } from '@/types/financial';
import { useUpdateBudgetPeriod } from '@/hooks/use-financial';

const formSchema = z.object({
  payment_day: z.number().min(1).max(31),
});

type FormData = z.infer<typeof formSchema>;

interface BudgetPeriodConfigFormProps {
  currentPeriod?: BudgetPeriod;
  onSuccess?: () => void;
  onCancel?: () => void;
  cancelLabel?: string;
  submitLabel?: string;
}

interface BudgetPeriodConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPeriod?: BudgetPeriod;
}

export function BudgetPeriodConfigForm({
  currentPeriod,
  onSuccess,
  onCancel,
  cancelLabel = 'Cancelar',
  submitLabel,
}: Readonly<BudgetPeriodConfigFormProps>) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate: updatePeriod } = useUpdateBudgetPeriod();

  const defaultPaymentDay = currentPeriod?.payment_day || 5;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      payment_day: defaultPaymentDay,
    },
  });

  useEffect(() => {
    form.reset({ payment_day: defaultPaymentDay });
  }, [defaultPaymentDay, form]);

  const createMutation = useMutation({
    mutationFn: (data: { payment_day: number }) =>
      budgetPeriodService.create(data),
    onSuccess: async (createdPeriod) => {
      queryClient.setQueryData(['financial', 'budgetPeriods', 'current'], createdPeriod);
      await queryClient.invalidateQueries({ queryKey: ['financial'] });
      await queryClient.refetchQueries({ queryKey: ['financial', 'budgetPeriods', 'current'] });
      await queryClient.refetchQueries({ queryKey: ['financial', 'budgetPeriods', 'navigation'] });
      await queryClient.refetchQueries({ queryKey: ['financial', 'settings'] });
      toast({
        title: 'Período Orçamentário criado!',
        description: 'Seu novo período foi configurado com sucesso.',
      });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : 'Ocorreu um erro ao criar o período.';
      toast({
        title: 'Erro ao criar período',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    if (currentPeriod) {
      updatePeriod(
        {
          id: currentPeriod.id,
          data: {
            payment_day: data.payment_day,
          }
        },
        {
          onSuccess: () => onSuccess?.(),
          onSettled: () => setIsSubmitting(false),
        }
      );
      return;
    }

    try {
      await createMutation.mutateAsync({
        payment_day: data.payment_day,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="payment_day"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                Data de início do período orçamentário
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Selecione o dia do mês que marcará o início do seu período orçamentário.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </FormLabel>
              <Select
                value={String(field.value)}
                onValueChange={(v) => field.onChange(Number(v))}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o dia" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={String(day)}>
                      Dia {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitLabel ?? (currentPeriod ? 'Salvar' : 'Criar Período')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

export function BudgetPeriodConfigModal({
  open,
  onOpenChange,
  currentPeriod,
}: Readonly<BudgetPeriodConfigModalProps>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            Configurar Período Orçamentário
          </DialogTitle>
          <DialogDescription className="text-left">
            É o intervalo de 30 dias entre a data prevista de recebimento de uma receita e outra. Informe a data esperada da sua receita principal. Esta definirá o seu Período Orçamentário. Por exemplo, se você espera receber no dia 5, seu período vai do dia 5 de um mês até o dia 4 do mês seguinte. Evite trocar o período a cada mês.
          </DialogDescription>
        </DialogHeader>

        <BudgetPeriodConfigForm
          currentPeriod={currentPeriod}
          onCancel={() => onOpenChange(false)}
          onSuccess={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

export default BudgetPeriodConfigModal;
